import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, MapPin, Search, ChevronLeft, ChevronRightIcon, ArrowLeft } from 'lucide-react';
import Header from '../../components/Header';
import { useAuth } from '../../context/AuthContext';
import { venuesAPI } from '../../utils/api';
import { handleApiError } from '../../utils/api';

const PAGE_SIZE = 20;

const ManageVenuesPage = () => {
  const navigate = useNavigate();
  const { userRole } = useAuth();

  const [venues, setVenues] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [deletingId, setDeletingId] = useState(null);

  const loadVenues = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await venuesAPI.getAll();
      setVenues(response.data || []);
    } catch (err) {
      const { error: errTitle, details } = handleApiError(err);
      setError(`${errTitle} : ${details}`);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadVenues();
  }, [loadVenues]);

  if (userRole !== 'admin') {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-red-600 mb-4">Accès refusé</h2>
            <p className="text-gray-600 mb-6">Vous devez être administrateur pour accéder à cette page.</p>
            <button
              onClick={() => navigate('/')}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition"
            >
              Retour à l'accueil
            </button>
          </div>
        </div>
      </div>
    );
  }

  const handleDelete = async (venue) => {
    if (!window.confirm(`Supprimer "${venue.name}" ? Cette action est irréversible.`)) {
      return;
    }
    setDeletingId(venue.id);
    try {
      await venuesAPI.delete(venue.id);
      setVenues((prev) => prev.filter((v) => v.id !== venue.id));
    } catch (err) {
      const { error: errTitle, details } = handleApiError(err);
      alert(`Impossible de supprimer : ${errTitle} - ${details}`);
    } finally {
      setDeletingId(null);
    }
  };

  const filteredVenues = venues.filter((venue) => {
    const term = searchTerm.toLowerCase();
    return (
      venue.name?.toLowerCase().includes(term) ||
      venue.city?.toLowerCase().includes(term) ||
      venue.country?.toLowerCase().includes(term)
    );
  });

  const totalPages = Math.max(1, Math.ceil(filteredVenues.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paginatedVenues = filteredVenues.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto">
          <button
            onClick={() => navigate('/admin')}
            className="text-gray-600 hover:text-blue-600 flex items-center gap-1 mb-4 text-sm"
          >
            <ArrowLeft size={16} />
            Retour à l'administration
          </button>

          {/* Header */}
          <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-4">
              <div>
                <h1 className="text-3xl font-bold text-gray-800 flex items-center">
                  <MapPin className="mr-3" size={32} />
                  Gestion des Lieux
                </h1>
                <p className="text-gray-600 mt-2">
                  {venues.length} lieu{venues.length > 1 ? 'x' : ''} au total
                </p>
              </div>
              <button
                onClick={() => navigate('/admin/venues/create')}
                className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition flex items-center"
              >
                <Plus className="mr-2" size={20} />
                Nouveau Lieu
              </button>
            </div>

            {/* Recherche */}
            <div className="relative">
              <Search className="absolute left-3 top-3 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Rechercher par nom, ville ou pays..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(1);
                }}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 mb-6">
              {error}
            </div>
          )}

          {isLoading ? (
            <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
              Chargement des lieux...
            </div>
          ) : filteredVenues.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center">
              <p className="text-gray-500">Aucun lieu trouvé</p>
            </div>
          ) : (
            <>
              <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-100 text-left text-sm text-gray-600">
                    <tr>
                      <th className="px-4 py-3">Nom</th>
                      <th className="px-4 py-3">Ville</th>
                      <th className="px-4 py-3">Pays</th>
                      <th className="px-4 py-3">Statut</th>
                      <th className="px-4 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {paginatedVenues.map((venue) => (
                      <tr key={venue.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-semibold text-gray-800">{venue.name}</td>
                        <td className="px-4 py-3 text-gray-600">{venue.city || '—'}</td>
                        <td className="px-4 py-3 text-gray-600">{venue.country || '—'}</td>
                        <td className="px-4 py-3">
                          {venue.isActive ? (
                            <span className="bg-green-100 text-green-800 text-xs font-semibold px-2 py-1 rounded-full">
                              Actif
                            </span>
                          ) : (
                            <span className="bg-gray-100 text-gray-600 text-xs font-semibold px-2 py-1 rounded-full">
                              Inactif
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => navigate(`/admin/venues/edit/${venue.id}`)}
                              className="bg-blue-600 text-white p-2 rounded-lg hover:bg-blue-700 transition"
                              title="Modifier"
                            >
                              <Edit size={16} />
                            </button>
                            <button
                              onClick={() => handleDelete(venue)}
                              disabled={deletingId === venue.id}
                              className="bg-red-600 text-white p-2 rounded-lg hover:bg-red-700 transition disabled:opacity-50"
                              title="Supprimer"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-gray-600">
                    Page {currentPage} sur {totalPages} ({filteredVenues.length} résultats)
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="bg-white border border-gray-300 p-2 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft size={18} />
                    </button>
                    <button
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="bg-white border border-gray-300 p-2 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronRightIcon size={18} />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManageVenuesPage;
